import React, { useState, useEffect, useRef, useMemo } from 'react'

import {
  EditOutlined,
  GroupOutlined,
  CloseOutlined,
  CheckOutlined,
  LinkOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  UndoOutlined,
  FileAddOutlined,
  SettingOutlined,
  DownOutlined,
  PrinterOutlined,
  DownloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  LoadingOutlined,
  WarningOutlined,
  HistoryOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useSmartBack from '@hooks/shared/useSmartBack'
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions'
import { translateErrorInterpolations } from '@utils/translateErrorInterpolations'
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler'
import {
  ORDER_ITEM_SECONDARY_COLUMNS,
  type OrderItemSecondaryColumn,
} from '@utils/orders/orderItemSecondaryColumns'

// Which secondary columns render as currency (right-aligned + $-prefixed) on
// the order show page. The other entries from ORDER_ITEM_SECONDARY_COLUMNS
// render as plain text.
const SECONDARY_COLUMN_IS_CURRENCY = new Set<OrderItemSecondaryColumn>([
  'price',
  'original_price',
  'cost',
])
import {
  CustomAttributesTags,
  hasVisibleCustomAttributes,
} from '@components/shared/inventory_attribute_definitions/CustomAttributesTags'
import ListPageHeader from '@shared/ListPageHeader'
import { SHIPMENT_STATUS_COLORS } from '@shared/shipments/statusColors'
import { useShipmentStatusLabel } from '@constants/shipment'
import OrderStatusLabel from '@shared/orders/OrderStatusLabel'
import FulfillmentsPanel from '@shared/orders/FulfillmentsPanel'
import ReprintLabelModal, { REPRINTABLE_FULFILLMENT_STATUSES } from '@shared/orders/ReprintLabelModal'
import HoldOrderModal from '@shared/orders/HoldOrderModal'
import ReleaseHoldModal from '@shared/orders/ReleaseHoldModal'
import IntegrationLogPanel from './IntegrationLogPanel'
import { ItemsSnapshotView, type ItemSnapshot } from '@shared/histories/ItemsSnapshotView'
import ValueAddedServicesCard from '@shared/ValueAddedServicesCard'
import VideoSection from '@shared/VideoSection'
import OrderAttachments from '@shared/orders/OrderAttachments'
import { Card, DatePicker, Descriptions, Divider, Drawer, Radio, Table, Tag, Space, Button, Alert, Tabs, Timeline, List, Empty, Modal, Form, Input, Tooltip, message, Dropdown, Collapse } from 'antd'
import FormReceiverInformation from '@shared/orders/FormReceiverInformation'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Link, useNavigate, useRevalidator } from 'react-router-dom'

import { formatDateTime, formatDate } from '../../../utils/dateTime'
import { formatAmount } from '@utils/currency'
import useInventoryModal from '@hooks/shared/useInventoryModal'
import { Can, CanShow, useCan } from '@shared/permissions'

interface CableSubscriptionCallbacks {
  received?: (data: any) => void
  [key: string]: unknown
}

interface CableManager {
  subscribe: (channelName: string, params: Record<string, unknown>, callbacks: CableSubscriptionCallbacks) => () => void
}

interface OrderShowProps {
  role?: 'admin' | 'operator'
  order: any
  orderService: any
  shipmentService: any
  thirdPartyApiLogService: any
  webhookDeliveryService: any
  partnerApiLogService: any
  valueAddedServiceService?: any // 用於搜尋加值服務範本
  cableManager: CableManager // operatorCable or adminCable
  addJob?: (jobId: string, jobName: string) => void // 背景任務管理器的 addJob 函數
  merchantId?: string // 🔥 Admin 需要傳入 merchantId 用於 WebSocket 訂閱
  organizationId?: string // 🔥 Operator 需要傳入 organizationId 用於 WebSocket 訂閱
  currentWarehouse?: any // Operator 當前操作倉庫
  contactAddressService: any // 常用地址服務（修正資訊 Modal 用）
}

// 操作歷史 fields_changed 區塊的值格式化：null / '' → '-'、boolean → 圖示、其他 String()
function formatHistoryValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') {
    return value
      ? <CheckOutlined style={{ color: 'green' }} />
      : <CloseOutlined style={{ color: 'lightgrey' }} />;
  }
  return String(value);
}

// 操作歷史 fields_changed 的 enum 值翻譯：用 sentinel 比對避免 missing key 訊息洩漏
function translateHistoryValue(
  t: TFunction,
  field: string,
  val: unknown,
): string | number | boolean | null | undefined {
  if (val === null) return null;
  if (val === undefined) return undefined;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val;
  if (val === '') return '';
  const stringVal = String(val);
  const sentinel = `__missing_${field}_${stringVal}__`;
  const translated = t(`models.order.${field}_values.${stringVal}`, { defaultValue: sentinel });
  return translated !== sentinel ? translated : stringVal;
}

const OrderShow: React.FC<OrderShowProps> = ({
  role = 'admin',
  order: initialOrder,
  orderService,
  shipmentService,
  thirdPartyApiLogService,
  webhookDeliveryService,
  partnerApiLogService,
  valueAddedServiceService,
  cableManager,
  addJob,
  merchantId,
  organizationId,
  currentWarehouse,
  contactAddressService,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = useSmartBack()
  const revalidator = useRevalidator()
  const can = useCan()
  const shipmentStatusLabel = useShipmentStatusLabel()
  const [order, setOrder] = useState<any>(initialOrder)
  const [error, setError] = useState<string | null>(null)
  const [testPrinting, setTestPrinting] = useState(false)
  const [reprintModalVisible, setReprintModalVisible] = useState(false)
  const [holdModalVisible, setHoldModalVisible] = useState(false)
  const [releaseModalVisible, setReleaseModalVisible] = useState(false)
  const [reprinting, setReprinting] = useState(false)
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [holding, setHolding] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [correctModalVisible, setCorrectModalVisible] = useState(false)
  const [correcting, setCorrecting] = useState(false)
  const [correctForm] = Form.useForm()
  const [apiLogsTabActive, setApiLogsTabActive] = useState(false)
  const [historyDrawerShipment, setHistoryDrawerShipment] = useState<any>(null)
  const subscriptionRef = useRef<(() => void) | null>(null)
  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  // 拉 inventory_attribute_definitions API 回的 name → label，映成 lookup map
  // 餵給 <CustomAttributesTags>，避免直接顯示英文 raw key（例：manufacturing_plant）。
  const orderMerchantId = order?.merchant?.id || order?.merchant_id || merchantId
  const attributeDefinitions = useInventoryAttributeDefinitions(role, orderMerchantId)
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  )

  const {
    inventoryModalColumns,
    showAllInventories,
    renderInventoryModals,
  } = useInventoryModal({ attributeLabelByName })

  // 同步 initialOrder 變化（當路由參數改變時）
  useEffect(() => {
    if (initialOrder) {
      setOrder(initialOrder)
    }
  }, [initialOrder])

  // Cleanup WebSocket subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current() // Call the unsubscribe function
        subscriptionRef.current = null
      }
    }
  }, [])

  // Action Handlers
  const handleCancel = () => {
    // 合併出貨單：取消前會先把合併出貨單還原成各自獨立的出貨單，需在 modal 明確告知操作者。
    const mergedSiblingCount = correctionSiblingOrders.length
    // 有 processing / shipped 的 FL → 後端 handle_cancel 不會動到它們:
    //   - release_allocated_inventories 用 FULFILLMENT_INVENTORY_MUTABLE_STATUSES,不收 processing / shipped
    //   - shipped FL 的 AASM cancel from-state 不收 :shipped,自然跳過
    //   - processing FL:Fulfillment AASM cancel from-state **包含** :processing(歷史設計給 STO cancel 用),
    //     所以後端 handle_cancel 內額外加 `next if fulfillment.processing?` 才守得住
    // 為什麼這條會被觸發到:在規則重排前 check_order_status precedence 把 [processing + OOS] 投影成 :out_of_stock,
    // 讓 cancel 按鈕亮、modal 開,FL 列表裡藏著 processing FL。新規則(processing 上移)修正後此場景 order 為 :processing,
    // cancel 按鈕本身就不會出現,modal partial 分支現實上不可達 — 留著當 defensive 提示。
    // partial 訊息優先於 merged,因為前者影響更廣(processing/shipped 不會被中斷)。
    const hasUncancelableFulfillment = (order?.fulfillments || []).some(
      (f: any) => f.status === 'processing' || f.status === 'shipped'
    )
    let content
    if (hasUncancelableFulfillment) {
      content = t(`shared.orders.detail.actions.cancel_confirm_content_partial`, { orderName: order.name })
    } else if (mergedSiblingCount > 0) {
      content = t(`shared.orders.detail.actions.cancel_confirm_content_merged`, { orderName: order.name, count: mergedSiblingCount })
    } else {
      content = t(`shared.orders.detail.actions.cancel_confirm_content`, { orderName: order.name })
    }
    Modal.confirm({
      title: t(`shared.orders.detail.actions.cancel_confirm_title`),
      icon: <ExclamationCircleOutlined />,
      content,
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await orderService.cancel(order.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.actions.cancel_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to cancel order:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }
  const handleResume = () => {
    Modal.confirm({
      title: t(`shared.orders.detail.actions.resume_confirm_title`),
      icon: <ExclamationCircleOutlined />,
      content: t(`shared.orders.detail.actions.resume_confirm_content`, { orderName: order.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await orderService.resume(order.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.actions.resume_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to resume order:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }
  // 修正資訊 Modal（自動攔截後開啟）
  const openCorrectModal = async () => {
    // 如果訂單尚未攔截但可攔截，自動執行攔截
    if (!order.is_held && order.holdable) {
      try {
        setHolding(true)
        const data = await orderService.hold(order.id, {
          reason: 'manual_intercept',
          note: t('shared.orders.detail.correct_modal.auto_hold_reason')
        })
        if (!data.success) {
          message.error(data.message || t('common.error.operation_failed'))
          return
        }
        revalidator.revalidate()
      } catch (err) {
        console.error('Failed to auto-hold order:', err)
        message.error(t('common.error.operation_failed'))
        return
      } finally {
        setHolding(false)
      }
    }

    correctForm.setFieldsValue({
      receiver_name: order.receiver_name || '',
      phone: order.phone || '',
      email: order.email || '',
      country_code: order.country_code || '',
      state: order.state || '',
      city: order.city || '',
      district: order.district || '',
      address: order.address || '',
      postal_code: order.postal_code || '',
      convenience_store_code: order.convenience_store_code || '',
      note: order.note || '',
      scheduled_shipping_date: order.scheduled_shipping_date ? dayjs(order.scheduled_shipping_date) : null,
      delivery_date: order.delivery_date ? dayjs(order.delivery_date) : null,
      delivery_time: order.delivery_time || null,
    })
    setCorrectModalVisible(true)
  }

  // 同出貨群組（與本訂單共用 fulfillment 的其他訂單）——修正會一併連動，modal 需提示。
  // 資料來自訂單明細 payload 既有的 fulfillments[].sibling_orders（id/name），跨 fulfillment 去重。
  const correctionSiblingOrders = useMemo(() => {
    const fulfillments = (order?.fulfillments || []) as Array<{ sibling_orders?: Array<{ id: string; name: string }> }>
    const byId = new Map<string, { id: string; name: string }>()
    fulfillments.forEach(f => (f.sibling_orders || []).forEach(s => { if (s?.id) byId.set(s.id, s) }))
    return Array.from(byId.values())
  }, [order])

  // 實際送出修正 API（已通過確認彈窗）
  const submitCorrection = async (changedFields: Record<string, any>) => {
    try {
      setCorrecting(true)
      const data = await orderService.correct(order.id, { order: changedFields })
      if (data.success) {
        message.success(t('shared.orders.detail.correct_modal.success'))
        setCorrectModalVisible(false)
        revalidator.revalidate()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to correct order:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setCorrecting(false)
    }
  }

  const handleCorrectSubmit = async () => {
    const values = correctForm.getFieldsValue()
    // 格式化日期欄位
    const dateFields = ['scheduled_shipping_date', 'delivery_date']
    dateFields.forEach(key => {
      values[key] = values[key] ? values[key].format('YYYY-MM-DD') : null
    })
    // 只送出有變更的欄位
    const changedFields: Record<string, any> = {}
    Object.keys(values).forEach(key => {
      const newVal = values[key] ?? null
      const oldVal = order[key] ?? null
      if (newVal !== oldVal) {
        changedFields[key] = newVal
      }
    })

    if (Object.keys(changedFields).length === 0) {
      message.info(t('shared.orders.detail.correct_modal.no_changes'))
      setCorrectModalVisible(false)
      return
    }

    // 按確認後再跳一層確認彈窗，使用者同意才真的更新（合併出貨時提示會連動 N 筆關聯訂單）
    Modal.confirm({
      title: t('shared.orders.detail.correct_modal.confirm_title'),
      content: correctionSiblingOrders.length > 0
        ? t('shared.orders.detail.correct_modal.confirm_content_cascade', { count: correctionSiblingOrders.length })
        : t('shared.orders.detail.correct_modal.confirm_content'),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: () => submitCorrection(changedFields),
    })
  }

  const handleRecheckStock = async () => {
    try {
      const data = await orderService.recheckStock(order.id)
      if (data.success) {
        message.success(t(`shared.orders.detail.actions.recheck_stock_success`))
        revalidator.revalidate()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to recheck stock:', err)
      message.error(t('common.error.operation_failed'))
    }
  }
  const handlePostpone = () => {
    Modal.confirm({
      title: t(`shared.orders.detail.actions.postpone_confirm_title`),
      icon: <ExclamationCircleOutlined />,
      content: t(`shared.orders.detail.actions.postpone_confirm_content`, { orderName: order.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await orderService.postpone(order.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.actions.postpone_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to postpone order:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }
  const handleFinalizeDraft = () => {
    Modal.confirm({
      title: t(`shared.orders.detail.actions.finalize_draft_confirm_title`),
      icon: <ExclamationCircleOutlined />,
      content: t(`shared.orders.detail.actions.finalize_draft_confirm_content`, { orderName: order.name }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await orderService.convertToNormal(order.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.actions.finalize_draft_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to convert order:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }

  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false)
  const [duplicateLoading, setDuplicateLoading] = useState(false)
  const [duplicateName, setDuplicateName] = useState('')
  const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null)

  const handleDuplicateOpen = () => {
    setDuplicateName(`${order.name}-COPY`)
    setDuplicateNameError(null)
    setDuplicateModalVisible(true)
  }

  const handleDuplicateConfirm = async () => {
    const trimmedName = duplicateName.trim()
    if (!trimmedName || duplicateNameError) return
    try {
      setDuplicateLoading(true)

      const response = await orderService.duplicate(order.id, trimmedName)
      if (response.success) {
        message.success(t('shared.orders.detail.actions.duplicate_success'))
        setDuplicateModalVisible(false)
        navigate(`/orders/${response.data.id}`)
      } else {
        // 撞名 → 用翻譯後訊息做 inline 欄位錯誤讓操作者就地改編號；其餘後端 error code 走 toast，皆不外露 raw code。
        handleApiError(response, {
          t,
          fallbackMessage: t('common.error.operation_failed'),
          onError: (msg, code) => {
            if (code === 'order_name_already_exists') {
              setDuplicateNameError(msg)
            } else {
              message.error(msg)
            }
          },
        })
      }
    } catch (err) {
      handleNetworkError(err, { t, fallbackMessage: t('common.error.operation_failed') })
    } finally {
      setDuplicateLoading(false)
    }
  }

  const handleReleaseHolds = async (note: string) => {
    try {
      setReleasing(true)
      const data = await orderService.releaseHolds(order.id, note)
      if (data.success) {
        message.success(t(`shared.orders.detail.actions.release_holds_success`))
        setReleaseModalVisible(false)
        revalidator.revalidate()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to release holds:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setReleasing(false)
    }
  }

  const handleHold = async (values: any) => {
    try {
      setHolding(true)
      const data = await orderService.hold(order.id, values)
      if (data.success) {
        message.success(t(`shared.orders.detail.actions.hold_success`))
        setHoldModalVisible(false)
        revalidator.revalidate()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (err) {
      console.error('Failed to hold order:', err)
      message.error(t('common.error.operation_failed'))
    } finally {
      setHolding(false)
    }
  }

  // Reprint label handler - 使用 fulfillment 的 print_label API (operator only)
  const handleReprintLabel = async (fulfillmentId: string, quantity: number) => {
    try {
      if (role !== 'operator') {
        message.error(t('common.error.operation_failed'))
        return
      }

      setReprinting(true)

      const { fulfillmentService } = await import(/* @vite-ignore */ '@services/operator/fulfillmentService')

      const data = await fulfillmentService.printLabel(fulfillmentId, quantity, { reprint: true })

      if (data.success) {
        // Custom 物流且尚未人工上傳託運單 → 跳過標籤列印
        if (data.data.skipped) {
          message.info(t('shared.orders.detail.actions.reprint_label_skipped', { defaultValue: '此出貨單為自訂物流，尚無託運單可列印' }))
          setReprintModalVisible(false)
          setReprinting(false)
          return
        }

        const jobId = data.data.job_id
        message.info(t('shared.orders.detail.actions.reprint_label_started'))
        setReprintModalVisible(false)

        // 🔥 將任務加入背景任務管理器（讓導航列顯示進度）
        if (addJob) {
          addJob(jobId, 'shared.orders.detail.actions.reprint_label_title')
        }

        // Subscribe to WebSocket for progress
        subscribeToJobProgress(jobId, true) // true 表示這是重新列印
      } else {
        message.error(data.message || t('common.error.operation_failed'))
        setReprinting(false)
      }
    } catch (err) {
      console.error('Failed to reprint label:', err)
      message.error(t('common.error.operation_failed'))
      setReprinting(false)
    }
  }

  // Generate PDF handler
  const handleGeneratePdf = async () => {
    try {
      setGeneratingPdf(true)
      const data = await orderService.generatePdf(order.id)

      if (data.success) {
        const jobId = data.data.job_id
        message.info(t('shared.orders.detail.actions.generate_pdf_started'))

        // 🔥 將任務加入背景任務管理器（讓導航列顯示進度）
        if (addJob) {
          addJob(jobId, 'shared.orders.detail.actions.generate_pdf_title')
        }

        // Subscribe to WebSocket for progress
        subscribeToJobProgress(jobId, false, 'pdf') // 'pdf' 表示這是生成 PDF
      } else {
        message.error(data.message || t('common.error.operation_failed'))
        setGeneratingPdf(false)
      }
    } catch (err) {
      console.error('Failed to generate PDF:', err)
      message.error(t('common.error.operation_failed'))
      setGeneratingPdf(false)
    }
  }

  // Test print label handler (保留原有的測試功能)
  const handleTestPrintLabel = async () => {
    try {
      setTestPrinting(true)
      const data = await shipmentService.testPrintLabel(order.id)

      if (data.success) {
        const jobId = data.data.job_id
        message.info('開始產生測試標籤...')

        // 🔥 將任務加入背景任務管理器（讓導航列顯示進度）
        if (addJob) {
          addJob(jobId, '測試列印標籤')
        }

        // Subscribe to WebSocket for progress
        subscribeToJobProgress(jobId, false)
      } else {
        message.error(data.message || '產生標籤失敗')
        setTestPrinting(false)
      }
    } catch (err) {
      console.error('Failed to test print label:', err)
      message.error('產生標籤失敗')
      setTestPrinting(false)
    }
  }

  // Subscribe to job progress via WebSocket
  const subscribeToJobProgress = (jobId: string, isReprint: boolean = false, jobType: string = 'label') => {
    if (!cableManager) {
      console.error('Cable manager not provided')
      if (jobType === 'pdf') {
        setGeneratingPdf(false)
      } else if (isReprint) {
        setReprinting(false)
      } else {
        setTestPrinting(false)
      }
      return
    }

    // Unsubscribe existing subscription if any
    if (subscriptionRef.current) {
      subscriptionRef.current() // Call the unsubscribe function
    }

    // Subscribe using cable manager
    // 🔥 根據角色決定訂閱參數
    const subscribeParams = role === 'admin'
      ? { merchant_id: merchantId }
      : {}; // Operator 不需要參數

    subscriptionRef.current = cableManager.subscribe(
      'NotificationChannel',
      subscribeParams,
      {
        received: (data) => {
          // 🔥 過濾：只處理匹配的 job_id
          if (data.job_id !== jobId && data.job_id !== String(jobId)) {
            return;
          }

          if (data.status === 'completed') {
            // ✅ 處理完成（BaseProgressWorker 的 data 欄位即為 perform_work 返回的資料）
            if (jobType === 'pdf') {
              // PDF 生成完成（BackgroundJobManager 會透過 download_url 自動開啟）
              message.success(t('shared.orders.detail.actions.generate_pdf_success'))
              setGeneratingPdf(false)
            } else if (isReprint) {
              // 重新列印：可能是單筆或批次結果
              const result = data.data
              if (result?.success_items) {
                // 批次列印完成，自動下載所有標籤
                result.success_items.forEach((item: any) => {
                  const pdfUrl = item.pdf_url || item.download_url
                  if (pdfUrl) {
                    window.open(pdfUrl, '_blank')
                  }
                })
                message.success(t('shared.orders.detail.actions.reprint_label_success', {
                  count: result.success_count
                }))
              } else if (result?.pdf_url) {
                // 單筆列印成功，開啟 PDF
                window.open(result.pdf_url, '_blank')
                message.success(t('shared.orders.detail.actions.reprint_label_complete'))
              } else {
                message.success(t('shared.orders.detail.actions.reprint_label_complete'))
              }
              setReprinting(false)
            } else {
              // 測試列印：單個標籤
              const result = data.data
              const pdfUrl = result?.pdf_url || result?.download_url || result?.url
              if (pdfUrl) {
                window.open(pdfUrl, '_blank')
                message.success('標籤已產生')
              } else {
                message.success('標籤產生完成')
              }
              setTestPrinting(false)
            }

            revalidator.revalidate() // Reload to show new shipping

            if (subscriptionRef.current) {
              subscriptionRef.current() // Unsubscribe
              subscriptionRef.current = null
            }
          } else if (data.status === 'failed') {
            // ❌ 任務失敗 — data.error 是 i18n code，data.interpolations 帶 i18n 插值
            // interpolations 裡的 provider/role/fields/error 需先翻譯（i18next nested 預設被 skipOnVariables 擋掉）
            const interpolations = translateErrorInterpolations(t, data.interpolations)
            const errorKey = data.error ? `common.error.${data.error}` : null
            const errorMsg = errorKey
              ? t(errorKey, { ...interpolations, defaultValue: data.error })
              : t('common.error.operation_failed')
            message.error(errorMsg as string)
            if (jobType === 'pdf') {
              setGeneratingPdf(false)
            } else if (isReprint) {
              setReprinting(false)
            } else {
              setTestPrinting(false)
            }
            if (subscriptionRef.current) {
              subscriptionRef.current() // Unsubscribe
              subscriptionRef.current = null
            }
          }
        }
      }
    )
  }
  // Inventory Status Tag
  const getInventoryStatusTag = (inventoryStatus: string) => {
    const statusConfig = {
      pending: { color: 'default', text: t(`enums.inventory_status.pending`) },
      allocated: { color: 'success', text: t(`enums.inventory_status.allocated`) },
      partial_allocated: { color: 'warning', text: t(`enums.inventory_status.partial_allocated`) },
      out_of_stock: { color: 'error', text: t(`enums.inventory_status.out_of_stock`) },
      partial_shipped: { color: 'warning', text: t(`enums.inventory_status.partial_shipped`) },
      shipped: { color: 'success', text: t(`enums.inventory_status.shipped`) },
      non_inventoried: { color: 'blue', text: t(`enums.inventory_status.non_inventoried`) },
      canceled: { color: 'default', text: t(`enums.inventory_status.canceled`) },
    }
    const config = (statusConfig as Record<string, { color: string; text: string }>)[inventoryStatus] || { color: 'default', text: inventoryStatus || '-' }
    return <Tag color={config.color}>{config.text}</Tag>
  }
  // 預處理 order_items：排序讓同 bundle 的品項相鄰，計算 rowSpan
  const { processedOrderItems, bundleRowSpanMap } = useMemo(() => {
    const items = order?.order_items || []
    // 分離 bundle items 和一般 items，保留原始順序
    const bundleItems = items.filter((i: any) => i.order_bundle)
    const regularItems = items.filter((i: any) => !i.order_bundle)

    // bundle items 按 bundle.id 分組
    const bundleGrouped: Record<string, any[]> = {}
    bundleItems.forEach((item: any) => {
      const key = item.order_bundle.id
      if (!bundleGrouped[key]) bundleGrouped[key] = []
      bundleGrouped[key].push(item)
    })

    // 重新排列：bundle groups 在前，一般 items 在後
    const sorted = [...Object.values(bundleGrouped).flat(), ...regularItems]

    // 計算每個 bundle group 的 rowSpan
    const spanMap: Record<string, { firstIndex: number; span: number }> = {}
    sorted.forEach((item, idx) => {
      if (!item.order_bundle) return
      const key = item.order_bundle.id
      if (spanMap[key] === undefined) {
        // 第一筆：rowSpan = group size
        spanMap[key] = { firstIndex: idx, span: bundleGrouped[key].length }
      }
    })

    return { processedOrderItems: sorted, bundleRowSpanMap: spanMap }
  }, [order?.order_items])

  // 彙整所有 order items 的庫存佔用
  const allOrderInventories = useMemo(() => {
    return (processedOrderItems || []).flatMap((item: any) =>
      (item.inventories || []).map((inv: any) => ({
        ...inv,
        product_sku: item.product?.sku || item.unknown_product?.sku || '-',
        product_name: item.product?.name || item.unknown_product?.name || '-',
      }))
    )
  }, [processedOrderItems])

  // Order Items Table Columns
  const orderItemColumns = [
    {
      title: t('shared.orders.form.bundle.tag'),
      key: 'bundle',
      width: 120,
      onCell: (record: any, index: number) => {
        if (!record.order_bundle) return {}
        const group = bundleRowSpanMap[record.order_bundle.id]
        if (!group) return {}
        if (index === group.firstIndex) {
          return { rowSpan: group.span }
        }
        return { rowSpan: 0 }
      },
      render: (_: any, record: any) => {
        if (!record.order_bundle) return null
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <GroupOutlined style={{ color: '#1677ff', fontSize: 12 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1d2129' }}>
                {record.order_bundle.sku}
              </span>
            </div>
            <span style={{ fontSize: 11, color: '#86909c' }}>
              {record.order_bundle.name}
            </span>
          </div>
        )
      },
    },
    {
      title: t(`models.order_item.product_name`),
      key: 'product_name',
      render: (_: any, record: any) => {
        // unknown_product：name + SKU 一起呈現，並附 mapping 警示
        if (record.unknown_product) {
          const unknownProductId = record.unknown_product.id
          const displayName = record.unknown_product.display_identifier || record.unknown_product.name
          const unknownSku = record.unknown_product.sku
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tag color="warning">{t('models.unknown_product._')}</Tag>
                <span style={{ color: '#faad14', fontWeight: 500 }}>{displayName}</span>
              </div>
              {unknownSku && (
                <span style={{ fontSize: 12, color: '#faad14' }}>{unknownSku}</span>
              )}
              <Alert
                message={t('models.unknown_product.unknown_product_warning')}
                type="warning"
                showIcon
                style={{ marginTop: '4px' }}
                action={
                  <Link to={`/unknown_products/${unknownProductId}`}>
                    <Button size="small" type="link">
                      {t('models.unknown_product.map_this_product')}
                    </Button>
                  </Link>
                }
              />
            </div>
          )
        }

        // 正常 product：name + SKU 同一格，SKU 為小字 subtitle
        const productName = record.product?.name || record.product_name
        const productSku = record.product?.sku || record.product_sku
        const productId = record.product?.id

        const nameEl = productId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to={`/products/${productId}`}>{productName}</Link>
            <a
              href={`${urlPrefix}/products/${productId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }}
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ) : (
          <span>{productName}</span>
        )

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nameEl}
            {productSku && (
              <span style={{ fontSize: 12, color: '#86909c' }}>{productSku}</span>
            )}
          </div>
        )
      },
    },
    {
      title: t(`models.order_item.inventory_type`),
      key: 'inventory_type',
      render: (_: any, record: any) => {
        // Handle both object format (new) and string format (old)
        if (record.inventory_type) {
          if (typeof record.inventory_type === 'object') {
            return `${record.inventory_type.name} | ${record.inventory_type.code}`
          }
          return record.inventory_type
        }
        return '-'
      },
    },
    {
      title: t(`models.order_item.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
    },
    {
      title: t(`models.order_item.shipped_quantity`),
      dataIndex: 'shipped_quantity',
      key: 'shipped_quantity',
      align: 'right',
    },
    {
      title: t(`models.order_item.batch`),
      dataIndex: 'batch',
      key: 'batch',
      render: (batch: any) => batch || '-',
    },
    {
      title: t(`models.order_item.expiration_info`),
      key: 'expiration_info',
      render: (_: any, record: any) => {
        const mfg = record.manufacturing_date
        const exp = record.expiration_date
        const shippable = record.shippable_expiration_date
        if (!mfg && !exp && !shippable) return '-'
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, lineHeight: 1.4 }}>
            {mfg && <span><span style={{ color: '#86909c' }}>{t('models.order_item.manufacturing_date')}</span> {mfg}</span>}
            {exp && <span><span style={{ color: '#86909c' }}>{t('models.order_item.expiration_date')}</span> {exp}</span>}
            {shippable && <span><span style={{ color: '#86909c' }}>{t('models.order_item.shippable_expiration_date')}</span> {shippable}</span>}
          </div>
        )
      },
    },
    {
      title: t(`models.order_item.custom_attributes`),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (attrs: any) => <CustomAttributesTags attributes={attrs} attributeLabelByName={attributeLabelByName} />,
    },
    // Secondary columns: one entry per ORDER_ITEM_SECONDARY_COLUMNS so adding
    // a new column on the backend auto-appears here. Currency columns get
    // right-aligned + $-prefixed; everything else is plain text. Auto-hide
    // logic below (visibleOrderItemColumns) collapses any column whose rows
    // are all empty, so merchants who haven't enabled a column see no visible
    // change.
    ...ORDER_ITEM_SECONDARY_COLUMNS.map((col) => {
      const isCurrency = SECONDARY_COLUMN_IS_CURRENCY.has(col);
      return {
        title: t(`models.order_item.${col}`),
        dataIndex: col,
        key: col,
        ...(isCurrency ? { align: 'right' as const } : {}),
        render: (v: unknown) => {
          if (v == null || v === '') return '-';
          return isCurrency ? `$${v}` : (v as React.ReactNode);
        },
      };
    }),
    {
      title: t(`models.order_item.inventory_status`),
      dataIndex: 'status',
      key: 'status',
      render: (status: any, record: any) => {
        const allocated = record.inventories ? record.inventories.reduce((sum: number, i: any) => sum + (i.quantity || 0), 0) : 0
        const total = record.quantity || 0

        return (
          <Space direction="vertical" size={2}>
            {status ? getInventoryStatusTag(status) : null}
            {(allocated !== total) && (
              <span style={{ fontSize: '12px', color: '#888' }}>
                <span style={{ color: allocated > 0 ? '#faad14' : '#888', fontWeight: 'bold' }}>{allocated}</span>
                <span>{' / '}</span>
                <span>{total}</span>
              </span>
            )}
          </Space>
        )
      },
    },
  ]

  // Auto-hide columns where all rows are empty
  const visibleOrderItemColumns = useMemo(() => {
    if (!processedOrderItems || processedOrderItems.length === 0) return orderItemColumns;

    // Columns that can be hidden if all values are empty. Secondary columns
    // (price/note/identifier/original_price/internal_note/cost) are added
    // dynamically from ORDER_ITEM_SECONDARY_COLUMNS so a new backend column
    // automatically participates in auto-hide.
    const hideable: Record<string, (item: any) => unknown> = {
      bundle: (item: any) => item.order_bundle,
      batch: (item: any) => item.batch,
      expiration_info: (item: any) => item.manufacturing_date || item.expiration_date || item.shippable_expiration_date,
      custom_attributes: (item: any) => hasVisibleCustomAttributes(item.custom_attributes),
      ...Object.fromEntries(
        ORDER_ITEM_SECONDARY_COLUMNS.map((col) => [col, (item: any) => item[col]]),
      ),
    };

    return orderItemColumns.filter((col: any) => {
      const accessor = hideable[col.key];
      if (!accessor) return true; // Always show non-hideable columns
      return processedOrderItems.some((item: any) => {
        const val = accessor(item);
        return val !== null && val !== undefined && val !== '' && val !== '-';
      });
    });
  }, [processedOrderItems, orderItemColumns]);

  // Handle shipment download
  const handleShipmentDownload = (shipment: any) => {
    if (!shipment.logistic_label_path) {
      message.error(t('common.error.file_not_found'))
      return
    }

    // execute_html: 先同步開 about:blank popup 保留 user gesture（避免 async 後被 popup blocker 擋），
    // 再呼叫 API 取 signed URL 並 navigate 過去。
    if (shipment.file_type === 'execute_html') {
      const popup = window.open('about:blank', '_blank')
      if (!popup) {
        message.error(t('common.error.popup_blocked'))
        return
      }
      shipmentService.executeHtmlUrl(shipment.id).then((res: any) => {
        if (res.success && res.data?.url) {
          popup.location = res.data.url
        } else {
          popup.close()
          message.error(res.message || t('common.error.operation_failed'))
        }
      }).catch((err: any) => {
        popup.close()
        console.error('Failed to fetch execute_html url:', err)
        message.error(t('common.error.network_error'))
      })
    } else {
      // 其他類型直接打開 URL
      window.open(shipment.logistic_label_path, '_blank')
    }
  }

  // Handle shipment delete
  const handleShipmentDelete = (shipment: any) => {
    Modal.confirm({
      title: t(`shared.orders.detail.shipments.delete_confirm_title`),
      icon: <ExclamationCircleOutlined />,
      content: t(`shared.orders.detail.shipments.delete_confirm_content`, { trackingNumber: shipment.tracking_number || shipment.id }),
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await shipmentService.delete(shipment.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.shipments.delete_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to delete shipping:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }

  // Handle shipment restore
  const handleShipmentRestore = (shipment: any) => {
    Modal.confirm({
      title: t(`shared.orders.detail.shipments.restore_confirm_title`),
      icon: <UndoOutlined />,
      content: t(`shared.orders.detail.shipments.restore_confirm_content`, { trackingNumber: shipment.tracking_number || shipment.id }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          const data = await shipmentService.restore(shipment.id)
          if (data.success) {
            message.success(t(`shared.orders.detail.shipments.restore_success`))
            revalidator.revalidate()
          } else {
            message.error(data.message || t('common.error.operation_failed'))
          }
        } catch (err) {
          console.error('Failed to restore shipping:', err)
          message.error(t('common.error.operation_failed'))
        }
      },
    })
  }

  // Shipment Table Columns
  const shippingColumns = [
    {
      title: t(`models.shipment.tracking_number`),
      dataIndex: 'tracking_number',
      key: 'tracking_number',
      render: (text: any, record: any) => (
        <Space>
          {text}
          {record.deleted_at && (
            <Tag color="red">{t(`models.shipment.deleted`)}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t(`models.fulfillment.name`),
      dataIndex: 'fulfillment_name',
      key: 'fulfillment_name',
      render: (text: any) => text || '-',
    },
    {
      title: t(`models.shipment.status`),
      dataIndex: 'status',
      key: 'status',
      render: (status: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Tag color={SHIPMENT_STATUS_COLORS[status] || 'default'}>
            {shipmentStatusLabel(status)}
          </Tag>
          {record.latest_label_error?.error_code ? (() => {
            const err = record.latest_label_error
            const interp = translateErrorInterpolations(t, err.interpolations)
            // 先試 shipment_history-specific 翻譯（BACKEND.md §6.4），fallback 到 common.error 通用錯誤碼
            const text = t(`enums.shipment_history.${err.key}.${err.error_code}`, {
              ...interp,
              defaultValue: t(`common.error.${err.error_code}`, { ...interp, defaultValue: err.error_code })
            })
            return (
              <span style={{ color: '#ff4d4f', fontSize: 12, maxWidth: 280, display: 'inline-block', wordBreak: 'break-all' }}>
                {text as string}
              </span>
            )
          })() : record.latest_carrier_problem && (
            <span style={{ color: '#ff4d4f', fontSize: 12, maxWidth: 280, display: 'inline-block', wordBreak: 'break-all' }}>
              {record.latest_carrier_problem}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: t(`models.shipment.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
    },
    {
      title: t(`models.shipment.source`),
      dataIndex: 'source',
      key: 'source',
      render: (val: any) => val ? (
        <Tag color={({ system: 'blue', platform: 'green', manual_uploaded: 'orange' } as Record<string, string>)[val] || 'default'}>
          {t(`enums.shipment.source.${val}`, { defaultValue: val })}
        </Tag>
      ) : '-',
    },
    {
      title: t(`common.timestamps`),
      key: 'timestamps',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>{formatDateTime(record.created_at)}</span>
        </Space>
      ),
    },
    {
      title: t('shared.orders.detail.shipments.history_column'),
      key: 'status_histories',
      align: 'center',
      width: 100,
      render: (_: any, record: any) => {
        const histories = record.status_histories || []
        const hasTracking = !!record.tracking_number
        const hasHistory = histories.length > 0
        const disabled = !hasTracking || !hasHistory
        const tooltipKey = !hasTracking
          ? 'shared.orders.detail.shipments.history_disabled_no_tracking'
          : 'shared.orders.detail.shipments.history_disabled_no_data'
        const button = (
          <Button
            type="link"
            size="small"
            icon={<HistoryOutlined />}
            disabled={disabled}
            onClick={() => setHistoryDrawerShipment(record)}
          >
            {t('shared.orders.detail.shipments.history_button')}
          </Button>
        )
        return disabled ? <Tooltip title={t(tooltipKey)}>{button}</Tooltip> : button
      },
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      align: 'center',
      width: 180,
      render: (_: any, record: any) => {
        if (record.deleted_at) {
          return (
            <CanShow requiredPermission="orders:write">
              <Button
                type="link"
                size="small"
                icon={<UndoOutlined />}
                onClick={() => handleShipmentRestore(record)}
              >
                {t('common.button.restore')}
              </Button>
            </CanShow>
          )
        }
        if (record.label_status === 'pending') {
          return <LoadingOutlined style={{ color: '#1677ff' }} spin />
        }
        if (record.label_status === 'processing') {
          // carrier API / GCS upload in-flight：禁止刪除，避免 PrintService 後續 persist_result / persist_error 撞 RecordNotFound
          return <LoadingOutlined style={{ color: '#faad14' }} spin />
        }
        if (record.label_status === 'error') {
          return (
            <Space>
              <WarningOutlined style={{ color: '#faad14' }} />
              <CanShow requiredPermission="orders:write">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleShipmentDelete(record)}
                >
                  {t('common.button.delete')}
                </Button>
              </CanShow>
            </Space>
          )
        }
        return (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleShipmentDownload(record)}
              disabled={!record.logistic_label_path}
            >
              {t('common.button.download')}
            </Button>
            <CanShow requiredPermission="orders:write">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleShipmentDelete(record)}
              >
                {t('common.button.delete')}
              </Button>
            </CanShow>
          </Space>
        )
      },
    },
  ]

  if (error) {
    return (
      <div>
        <Alert
          message={t('common.error.title')}
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={goBack}>
              {t('common.button.back')}
            </Button>
          }
        />
      </div>
    )
  }
  if (!order) {
    return (
      <div>
        <Alert
          message={t(`shared.order_detail.not_found`)}
          type="warning"
          showIcon
        />
      </div>
    )
  }
  // ==================== 操作按鈕分組 ====================

  // 1. 主操作（裸按鈕，當前最需要做的事）
  const primaryActions = []

  if (order.editable) {
    primaryActions.push(
      <Can key="edit" requiredPermission="orders:write">
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/orders/${order.id}/edit`)}
        >
          {t('common.button.edit')}
        </Button>
      </Can>
    )
  }

  if (order.correctable) {
    primaryActions.push(
      <Can key="correct" requiredPermission="orders:write">
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={openCorrectModal}
          loading={holding}
        >
          {t('shared.orders.detail.actions.correct')}
        </Button>
      </Can>
    )
  }

  if (order.resumable) {
    primaryActions.push(
      <Can key="resume" requiredPermission="orders:write">
        <Button
          type="primary"
          icon={<UndoOutlined />}
          onClick={handleResume}
        >
          {t(`shared.orders.detail.actions.resume`)}
        </Button>
      </Can>
    )
  }

  // 2. 列印（輸出相關）
  const printMenuItems = []

  printMenuItems.push({
    key: 'order-pdf',
    label: t('shared.orders.detail.actions.generate_pdf'),
    icon: <DownloadOutlined />,
    onClick: handleGeneratePdf,
    disabled: generatingPdf,
    requiredPermission: 'orders:write',
  })

  const hasReprintableFulfillment = (order?.fulfillments || []).some(
    (f: any) => REPRINTABLE_FULFILLMENT_STATUSES.includes(f.status)
  )
  if (role === 'operator' && hasReprintableFulfillment) {
    printMenuItems.push({
      key: 'reprint-label',
      label: t('shared.orders.detail.actions.reprint_label'),
      icon: <PrinterOutlined />,
      onClick: () => setReprintModalVisible(true),
      disabled: reprinting,
      requiredPermission: 'orders:write',
    })
  }

  // 3. 更多操作（日常操作）
  const moreActions = []

  if (order.can_recheck_stock) {
    moreActions.push({
      key: 'recheck',
      label: t('shared.orders.detail.actions.recheck_stock'),
      icon: <ReloadOutlined />,
      onClick: handleRecheckStock,
      requiredPermission: 'orders:write',
    })
  }

  if (order.is_held) {
    moreActions.push({
      key: 'release-holds',
      label: t('shared.orders.detail.actions.release_holds'),
      icon: <CheckCircleOutlined />,
      onClick: () => setReleaseModalVisible(true),
      requiredPermission: 'orders:write',
    })
  }

  if (order.can_finalize_draft) {
    moreActions.push({
      key: 'convert',
      label: t('shared.orders.detail.actions.finalize_draft'),
      icon: <FileAddOutlined />,
      onClick: handleFinalizeDraft,
      requiredPermission: 'orders:write',
    })
  }

  moreActions.push({
    key: 'duplicate',
    label: t('shared.orders.detail.actions.duplicate'),
    icon: <CopyOutlined />,
    onClick: handleDuplicateOpen,
    requiredPermission: 'orders:write',
  })

  // 4. 異常管制（破壞性操作）
  const dangerActions = []

  if (order.holdable && !order.is_held) {
    dangerActions.push({
      key: 'hold',
      label: t('shared.orders.detail.actions.hold'),
      icon: <StopOutlined />,
      danger: true,
      onClick: () => setHoldModalVisible(true),
      requiredPermission: 'orders:write',
    })
  }

  // 延期訂單暫時隱藏（保留邏輯）
  if (false && order.postponable) {
    dangerActions.push({
      key: 'postpone',
      label: t('shared.orders.detail.actions.postpone'),
      icon: <FileAddOutlined />,
      onClick: handlePostpone,
      requiredPermission: 'orders:write',
    })
  }

  if (order.cancelable) {
    dangerActions.push({
      key: 'cancel',
      label: t('shared.orders.detail.actions.cancel'),
      icon: <CloseCircleOutlined />,
      danger: true,
      onClick: handleCancel,
      requiredPermission: 'orders:write',
    })
  }

  // 權限過濾：沒有權限的項目直接不出現在 dropdown
  const filterByPermission = (items: any[]) =>
    items.filter((item: any) => !item.requiredPermission || can(item.requiredPermission))
  const filteredPrintMenuItems = filterByPermission(printMenuItems)
  const filteredMoreActions = filterByPermission(moreActions)
  const filteredDangerActions = filterByPermission(dangerActions)

  // ValueAddedServicesCard / VideoPreview 仍是 .jsx，TS 從其 destructure 推導出的
  // Props 不完整（含 PropTypes Object 限制），cast 成 ComponentType<any> 保留原本 runtime 行為。
  // 放在 component body 內以避免 Vite HMR 下的 top-level TDZ。
  const ValueAddedServicesCardAny = ValueAddedServicesCard as React.ComponentType<any>
  // ListPageHeader 雖是 .tsx，但 title prop 宣告為 string；runtime 接受 ReactNode（如 <Space>...）
  // 為避免 Props 假宣告影響範圍擴散，這裡先 cast 放行；後續可考慮拓寬 ListPageHeader 的 title 型別。
  const ListPageHeaderAny = ListPageHeader as React.ComponentType<any>

  const basePath = role === 'operator' ? '/operator' : '/admin'
  const breadcrumbItems = ['orders', 'orders.list', { label: order.name }]

  const headerTitle = (
    <Space>
      <span>{order.name}</span>
      {order.urgent && <Tag color="red">{t('shared.order_detail.urgent')}</Tag>}
      <OrderStatusLabel status={order.status} />
    </Space>
  )

  const headerActions = (
    <>
      {primaryActions}

      {filteredPrintMenuItems.length > 0 && (
        <Dropdown menu={{ items: filteredPrintMenuItems }} trigger={['click']}>
          <Button icon={<PrinterOutlined />}>
            {t('common.button.print')} <DownOutlined />
          </Button>
        </Dropdown>
      )}

      {filteredMoreActions.length > 0 && (
        <Dropdown menu={{ items: filteredMoreActions }} trigger={['click']}>
          <Button icon={<SettingOutlined />}>
            {t('shared.orders.detail.sections.more_actions')} <DownOutlined />
          </Button>
        </Dropdown>
      )}

      {filteredDangerActions.length > 0 && (
        <Dropdown menu={{ items: filteredDangerActions }} trigger={['click']}>
          <Button icon={<StopOutlined />} danger>
            {t('shared.orders.detail.sections.exceptions')} <DownOutlined />
          </Button>
        </Dropdown>
      )}
    </>
  )

  return (
    <div>
      <ListPageHeaderAny
        items={breadcrumbItems}
        basePath={basePath}
        title={headerTitle}
        actions={headerActions}
      />

      {order.is_held && order.active_holds && order.active_holds.length > 0 && (
        <Alert
          message={t('shared.orders.detail.active_holds_title')}
          description={
            <List
              size="small"
              dataSource={order.active_holds}
              renderItem={(item: any) => (
                <List.Item>
                  <Space>
                    <Tag color="error">{t(`enums.order_hold.reason.${item.reason}`, { defaultValue: item.reason })}</Tag>
                    {item.note && <span style={{ fontWeight: 500 }}>{item.note}</span>}
                    {item.held_by_name ? (
                      <span style={{ color: '#666' }}>- {item.held_by_name}</span>
                    ) : (
                      <Tag>{t('models.order_hold.held_by_system')}</Tag>
                    )}
                    <span style={{ color: '#999', fontSize: '12px' }}>({formatDateTime(item.created_at)})</span>
                  </Space>
                </List.Item>
              )}
            />
          }
          type="error"
          showIcon
          icon={<StopOutlined />}
          style={{ marginBottom: '16px' }}
          action={
            <Can requiredPermission="orders:write">
              <Button size="small" type="primary" danger onClick={() => setReleaseModalVisible(true)}>
                {t('shared.orders.detail.actions.release_holds')}
              </Button>
            </Can>
          }
        />
      )}
      {/* Order Basic Information */}
        <Descriptions column={3} bordered size="small" style={{ marginBottom: '16px' }}>
          {role === 'operator' && (
            <Descriptions.Item label={t(`models.merchant.name`)}>
              {order.merchant?.name || order.merchant_name}
            </Descriptions.Item>
          )}
          {order.extra_name && (
            <Descriptions.Item label={t(`models.order.extra_name`)}>
              {order.extra_name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t(`models.order.status`)}>
            <OrderStatusLabel status={order.status} />
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.sales_channel`)}>
            {order.sales_channel?.name || order.channel?.name}
          </Descriptions.Item>
          {order.ec_account && (
            <Descriptions.Item label={t(`models.order.ec_account`)}>
              {`${order.ec_account.module_name} (${order.ec_account.display_name})`}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t(`models.order.shipping_type`)}>
            {order.shipping_type ? order.shipping_type.name : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.total_amount`)}>
            {formatAmount(order.total_amount, order.currency)} {order.currency}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.is_cod`)}>
            {order.is_cod ? (
              <CheckOutlined style={{ color: 'green' }} />
            ) : (
              <CloseOutlined style={{ color: 'lightgrey' }} />
            )}
          </Descriptions.Item>
          {order.delivery_with_pickup && (
            <Descriptions.Item label={t(`models.order.delivery_with_pickup`)}>
              <CheckOutlined style={{ color: 'green' }} />
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t(`common.created_at`)}>
            {formatDateTime(order.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.placed_at`)}>
            {formatDateTime(order.placed_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t(`common.updated_at`)}>
            {formatDateTime(order.updated_at)}
          </Descriptions.Item>
          {order.shipped_at && (
            <Descriptions.Item label={t(`models.order.shipped_at`)}>
              {formatDateTime(order.shipped_at)}
            </Descriptions.Item>
          )}
          {order.scheduled_shipping_date && (
            <Descriptions.Item label={t(`models.order.scheduled_shipping_date`)}>
              {formatDate(order.scheduled_shipping_date)}
            </Descriptions.Item>
          )}
          {order.expected_ship_date && (
            <Descriptions.Item label={t(`models.order.expected_ship_date`)}>
              <span style={{
                color: dayjs().isAfter(dayjs(order.expected_ship_date), 'day') && !['shipped', 'canceled'].includes(order.status) ? '#ff4d4f' : 'inherit',
                fontWeight: dayjs().isAfter(dayjs(order.expected_ship_date), 'day') && !['shipped', 'canceled'].includes(order.status) ? 'bold' : 'normal'
              }}>
                {formatDate(order.expected_ship_date)}
                {dayjs().isAfter(dayjs(order.expected_ship_date), 'day') && !['shipped', 'canceled'].includes(order.status) && (
                  <Tooltip title={t('shared.orders.list.overdue_warning')}>
                    <InfoCircleOutlined style={{ marginLeft: 4 }} />
                  </Tooltip>
                )}
              </span>
            </Descriptions.Item>
          )}
          {order.delivery_date && (
            <Descriptions.Item label={t(`models.order.delivery_date`)}>
              {formatDate(order.delivery_date)}{order.delivery_time ? ` (${t(`enums.order.delivery_time.${order.delivery_time.toLowerCase()}`)})` : ''}
            </Descriptions.Item>
          )}
          {order.picking_lists && order.picking_lists.length > 0 && (
            <Descriptions.Item label={t(`models.order.picking_lists`)}>
              <Space direction="vertical" size="small">
                {order.picking_lists.map((pl: any) => (
                  <div key={pl.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Link to={`/picking_lists/${pl.id}`}>
                      {pl.display_name}
                    </Link>
                    <a
                      href={`${urlPrefix}/picking_lists/${pl.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
                    </a>
                  </div>
                ))}
              </Space>
            </Descriptions.Item>
          )}
          {order.note && (
            <Descriptions.Item label={t(`models.order.note`)} span={3}>
              {order.note}
            </Descriptions.Item>
          )}
        </Descriptions>

      {/* Additional Information (只在有資料時顯示) */}
      {(order.merged_order || order.merged_from_orders?.length > 0 || order.seller_name || order.seller_phone || order.invoice_number) && (
        <Card
          title={t(`shared.orders.detail.sections.additional_info`)}
          style={{ marginBottom: '16px' }}
          size="small"
        >
          <Descriptions column={3} bordered size="small">
            {order.merged_order && (
              <Descriptions.Item label={t(`models.order.merged_into`)}>
                <Link to={`/orders/${order.merged_order.id}`}>
                  {order.merged_order.name}
                </Link>
              </Descriptions.Item>
            )}
            {order.merged_from_orders?.length > 0 && (
              <Descriptions.Item label={t(`models.order.merged_from_orders`)} span={3}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {order.merged_from_orders.map((sourceOrder: any) => (
                    <div key={sourceOrder.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Link to={`/orders/${sourceOrder.id}`}>
                        {sourceOrder.name}
                      </Link>
                      <OrderStatusLabel status={sourceOrder.status} />
                    </div>
                  ))}
                </Space>
              </Descriptions.Item>
            )}
            {order.seller_name && (
              <Descriptions.Item label={t(`models.order.seller_name`)}>
                {order.seller_name}
              </Descriptions.Item>
            )}
            {order.seller_phone && (
              <Descriptions.Item label={t(`models.order.seller_phone`)}>
                {order.seller_phone}
              </Descriptions.Item>
            )}
            {order.invoice_number && (
              <Descriptions.Item label={t(`models.order.invoice_number`)}>
                {order.invoice_number}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* Order Videos + recording session diagnostics (shared with FulfillmentShow) */}
      <VideoSection resourceId={order.id} service={orderService} addJob={addJob} />

      {/* Order Histories & API Logs */}
      <Card style={{ marginBottom: '16px' }} size="small">
        <Tabs
          defaultActiveKey="histories"
          size="small"
          onChange={(key) => {
            if (key === 'api_logs' && !apiLogsTabActive) {
              setApiLogsTabActive(true)
            }
          }}
          items={[
            {
              key: 'histories',
              label: t('shared.orders.detail.fields.histories'),
              children: order.histories && order.histories.length > 0 ? (
                <Timeline
                  mode="left"
                  items={order.histories.map((h: any) => {
                    // 上提 description 解析，這樣 fields_changed 區塊也能用到 parsedDescription.fields_changed
                    let parsedDescription: any = h.description;
                    if (typeof h.description === 'string') {
                      try { parsedDescription = JSON.parse(h.description); } catch (e) { /* not JSON */ }
                    }
                    const fieldsChanged: Record<string, unknown> | null =
                      (h.fields_changed && Object.keys(h.fields_changed).length > 0)
                        ? h.fields_changed
                        : (parsedDescription && typeof parsedDescription === 'object' && parsedDescription.fields_changed && Object.keys(parsedDescription.fields_changed).length > 0)
                          ? parsedDescription.fields_changed
                          : null;
                    return ({
                    key: h.id,
                    children: (
                      <div>
                        <div>
                          {(h.description && !(typeof h.description === 'object' && Object.keys(h.description).length === 0)) ? (
                            (() => {
                              const parsed = parsedDescription;
                              if (parsed && typeof parsed === 'object' && parsed.key) {
                                const { key, ...params } = parsed;
                                if ((key === 'hold_order' || key === 'release_hold') && params.reason) {
                                  params.reason = t(`enums.order_hold.reason.${params.reason}`, { defaultValue: params.reason });
                                }
                                // print_shipment_label_error 遵守 BACKEND.md §6.4：description = { key: action, error_code, interpolations }
                                if (key === 'print_shipment_label_error' && params.error_code) {
                                  const innerInterpolations = { ...(params.interpolations || {}) };
                                  if (innerInterpolations.provider) {
                                    innerInterpolations.provider = t(`enums.shipping_type.provider.${innerInterpolations.provider}`, { defaultValue: innerInterpolations.provider });
                                  }
                                  if (innerInterpolations.role) {
                                    innerInterpolations.role = t(`common.error.role.${innerInterpolations.role}`, { defaultValue: innerInterpolations.role });
                                  }
                                  if (innerInterpolations.fields) {
                                    innerInterpolations.fields = String(innerInterpolations.fields).split(',').map((f: any) => t(`common.error.address_field.${f.trim()}`, { defaultValue: f.trim() })).join('、');
                                  }
                                  if (innerInterpolations.error) {
                                    innerInterpolations.error = t(`common.error.${innerInterpolations.error}`, { defaultValue: innerInterpolations.error });
                                  }
                                  // 把翻譯好的具體錯誤訊息作為標題模板的 {{error_message}} 插值
                                  params.error_message = t(`common.error.${params.error_code}`, { defaultValue: params.error_code, ...innerInterpolations });
                                  delete params.error_code;
                                  delete params.interpolations;
                                }
                                // api_error 的 error 欄位若為 i18n key（平台 client 對應過），翻譯後再插值
                                if (key === 'api_error' && params.error) {
                                  params.error = t(`common.error.${params.error}`, { defaultValue: params.error });
                                }
                                if (key === 'edit_order_items' && (Array.isArray(params.before_items) || Array.isArray(params.after_items))) {
                                  return (
                                    <div>
                                      <strong>{t(`enums.order_history.action.${key}`, { defaultValue: key })}</strong>
                                      <ItemsSnapshotView
                                        beforeItems={(params.before_items as ItemSnapshot[]) ?? []}
                                        afterItems={(params.after_items as ItemSnapshot[]) ?? []}
                                        t={t}
                                        fieldKeyPrefix="models.order_item"
                                      />
                                    </div>
                                  );
                                }
                                // 拆分出貨任務（手動 / 平台抓單）：清楚標示「原 / 新拆出」+ items 列表
                                if (key === 'split_fulfillment' || key === 'platform_split_fulfillment') {
                                  // 優先顯示 Fulfillment#name，fallback 到 number。
                                  // 舊 history 只寫 original_fulfillment_name（當時是 number-like 識別字串）→ fallback 順序仍能 catch。
                                  const originalNumber = params.original_fulfillment_name || params.original_fulfillment_number;
                                  const newFulfillments = Array.isArray(params.new_fulfillments) ? params.new_fulfillments : [];
                                  const currency = params.currency ? ` ${params.currency}` : '';
                                  return (
                                    <div>
                                      <strong>{t(`enums.order_history.action.${key}`, { defaultValue: key })}</strong>
                                      <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                          <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>{t('shared.orders.detail.histories.split_source_label', { defaultValue: '原' })}</Tag>
                                          <strong>{originalNumber}</strong>
                                          {params.original_total_amount != null && (
                                            <span style={{ color: '#999' }}>{t('shared.orders.detail.histories.split_original_label', { defaultValue: '原金額' })}: {formatAmount(params.original_total_amount, params.currency)}{currency}</span>
                                          )}
                                          {params.original_remaining_amount != null && (
                                            <span style={{ color: '#999' }}>{t('shared.orders.detail.histories.split_remaining_label', { defaultValue: '剩餘金額' })}: {formatAmount(params.original_remaining_amount, params.currency)}{currency}</span>
                                          )}
                                        </div>
                                        {newFulfillments.map((nf: any) => (
                                          <div key={nf.id} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '4px' }}>
                                            <Tag color="processing" style={{ fontSize: '11px', margin: 0 }}>{t('shared.orders.detail.histories.split_new_label', { defaultValue: '新拆出' })}</Tag>
                                            <strong>{nf.name || nf.number}</strong>
                                            {nf.total_amount != null && (
                                              <span style={{ color: '#999' }}>{t('models.fulfillment.total_amount', { defaultValue: '金額' })}: {formatAmount(nf.total_amount, params.currency)}{currency}</span>
                                            )}
                                          </div>
                                        ))}
                                        {Array.isArray(params.items) && params.items.length > 0 && (
                                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #f0f0f0' }}>
                                            <strong>{t('shared.orders.detail.histories.split_items_label', { defaultValue: '拆走品項' })}：</strong>
                                            <span style={{ marginLeft: '4px', color: '#666' }}>
                                              {params.items.map((it: any) => `${it.sku || it.id} × ${it.quantity ?? '?'}`).join(', ')}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                // 合併出貨任務：source FL + 金額 → target FL（含 target 併入前/後金額）
                                if (key === 'merge_into') {
                                  // 優先顯示 Fulfillment#name，fallback 到 number；舊 history 沒 name 鍵→自動 fallback。
                                  const sourceNumber = params.source_fulfillment_name || params.source_fulfillment_number;
                                  const targetNumber = params.target_fulfillment_name || params.target_fulfillment_number;
                                  const currencySuffix = params.currency ? ` ${params.currency}` : '';
                                  const amountLabel = t('models.fulfillment.total_amount', { defaultValue: '金額' });
                                  return (
                                    <div>
                                      <strong>{t('enums.order_history.action.merge_fulfillment', { defaultValue: 'merge_fulfillment' })}</strong>
                                      <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                          <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>{t('shared.orders.detail.histories.merge_source_label', { defaultValue: '來源' })}</Tag>
                                          <strong>{sourceNumber}</strong>
                                          {params.source_total_amount != null && (
                                            <span style={{ color: '#999' }}>{amountLabel}: {formatAmount(params.source_total_amount, params.currency)}{currencySuffix}</span>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                                          <Tag color="processing" style={{ fontSize: '11px', margin: 0 }}>{t('shared.orders.detail.histories.merge_target_label', { defaultValue: '併入' })}</Tag>
                                          <strong>{targetNumber}</strong>
                                          {params.target_total_before != null && (
                                            <span style={{ color: '#999' }}>{t('shared.orders.detail.histories.merge_target_before_label', { defaultValue: '原金額' })}: {formatAmount(params.target_total_before, params.currency)}{currencySuffix}</span>
                                          )}
                                          {params.target_total_after != null && (
                                            <span style={{ color: '#999' }}>{t('shared.orders.detail.histories.merge_target_after_label', { defaultValue: '合併後金額' })}: {formatAmount(params.target_total_after, params.currency)}{currencySuffix}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                // 批次拆分出貨任務：target + per-fulfillment 結果 + 結尾摘要
                                if (key === 'batch_split_fulfillment' || key === 'batch_split_fulfillment_no_op') {
                                  const results = Array.isArray(params.results) ? params.results : [];
                                  const splitCount = results.filter((r: any) => r.status === 'split').length;
                                  const errorCount = results.filter((r: any) => r.status === 'error').length;
                                  const skippedCount = results.filter((r: any) => r.status === 'skipped').length;
                                  return (
                                    <div>
                                      <strong>{t(`enums.order_history.action.${key}`, { defaultValue: key })}</strong>
                                      <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}>
                                        {params.target_product_type && (
                                          <div>
                                            <strong>{t('shared.orders.detail.histories.batch_split_target_label', { defaultValue: '拆分目標' })}：</strong>
                                            <span style={{ marginLeft: '4px' }}>
                                              {t(`admin.orders.list.batch_panel.target_product_type.${params.target_product_type}`, { defaultValue: params.target_product_type })}
                                              {Array.isArray(params.product_skus) && params.product_skus.length > 0 && ` (${params.product_skus.join(', ')})`}
                                            </span>
                                          </div>
                                        )}
                                        {params.order_skip_reason && (
                                          <div style={{ marginTop: '4px', color: '#ff4d4f' }}>
                                            {t(`errors.${params.order_skip_reason}`, { defaultValue: params.order_skip_reason })}
                                          </div>
                                        )}
                                        {results.length > 0 && (
                                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #f0f0f0' }}>
                                            <div style={{ marginBottom: '4px', color: '#666' }}>
                                              {t('shared.orders.detail.histories.batch_split_summary', {
                                                defaultValue: '已拆 {{split}} / 失敗 {{error}} / 跳過 {{skipped}}',
                                                split: splitCount,
                                                error: errorCount,
                                                skipped: skippedCount
                                              })}
                                            </div>
                                            {results.map((r: any, idx: number) => {
                                              const newFls = Array.isArray(r.new_fulfillments) ? r.new_fulfillments : [];
                                              const items = Array.isArray(r.items) ? r.items : [];
                                              const sourceLabel = t('shared.orders.detail.histories.split_source_label', { defaultValue: '原' });
                                              const newSplitLabel = t('shared.orders.detail.histories.split_new_label', { defaultValue: '新拆出' });
                                              const originalLabel = t('shared.orders.detail.histories.split_original_label', { defaultValue: '原金額' });
                                              const remainingLabel = t('shared.orders.detail.histories.split_remaining_label', { defaultValue: '剩餘金額' });
                                              const amountLabel = t('models.fulfillment.total_amount', { defaultValue: '金額' });
                                              const itemsLabel = t('shared.orders.detail.histories.split_items_label', { defaultValue: '拆走品項' });
                                              const currencySuffix = r.currency ? ` ${r.currency}` : '';

                                              // 成功拆分：完全比照 split_fulfillment 單筆顯示
                                              // ([原] source row + [新拆出] new row + 拆走品項，無狀態 tag，狀態由頂部 summary 提供)
                                              if (r.status === 'split') {
                                                return (
                                                  <div key={idx} style={{ marginTop: idx === 0 ? 0 : '8px', paddingTop: idx === 0 ? 0 : '8px', borderTop: idx === 0 ? 'none' : '1px dashed #f0f0f0' }}>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                                      <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>{sourceLabel}</Tag>
                                                      <strong>{r.fulfillment_name || r.fulfillment_number || r.fulfillment_id}</strong>
                                                      {r.original_total_amount != null && (
                                                        <span style={{ color: '#999' }}>{originalLabel}: {formatAmount(r.original_total_amount, r.currency)}{currencySuffix}</span>
                                                      )}
                                                      {r.remaining_amount != null && (
                                                        <span style={{ color: '#999' }}>{remainingLabel}: {formatAmount(r.remaining_amount, r.currency)}{currencySuffix}</span>
                                                      )}
                                                    </div>
                                                    {newFls.map((nf: any) => (
                                                      <div key={nf.id} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '4px' }}>
                                                        <Tag color="processing" style={{ fontSize: '11px', margin: 0 }}>{newSplitLabel}</Tag>
                                                        <strong>{nf.name || nf.number}</strong>
                                                        {nf.total_amount != null && (
                                                          <span style={{ color: '#999' }}>{amountLabel}: {formatAmount(nf.total_amount, nf.currency || r.currency)}{nf.currency ? ` ${nf.currency}` : ''}</span>
                                                        )}
                                                      </div>
                                                    ))}
                                                    {items.length > 0 && (
                                                      <div style={{ marginTop: '4px' }}>
                                                        <strong>{itemsLabel}：</strong>
                                                        <span style={{ marginLeft: '4px', color: '#666' }}>
                                                          {items.map((it: any) => `${it.sku || it.id} × ${it.quantity ?? '?'}`).join(', ')}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              }

                                              // 失敗 / 跳過：簡單 status tag + 原因
                                              const tagColor = r.status === 'error' ? 'error' : 'default';
                                              const tagLabel = r.status === 'error'
                                                ? t('shared.orders.detail.histories.batch_split_status_error', { defaultValue: '失敗' })
                                                : t('shared.orders.detail.histories.batch_split_status_skipped', { defaultValue: '跳過' });
                                              return (
                                                <div key={idx} style={{ marginTop: idx === 0 ? 0 : '4px', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                                  <Tag color={tagColor} style={{ fontSize: '11px', margin: 0 }}>{tagLabel}</Tag>
                                                  <strong>{r.fulfillment_number || r.fulfillment_id}</strong>
                                                  {r.reason && <span style={{ color: '#999' }}>{t(`errors.${r.reason}`, { defaultValue: r.reason })}</span>}
                                                  {r.error_code && <span style={{ color: '#ff4d4f' }}>{t(`errors.${r.error_code}`, { defaultValue: r.error_code })}</span>}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                // Fulfillment 重建（Order/OrderItem 編輯觸發）：清楚標示 移除 / 建立
                                // 樣式與拆分出貨任務一致 — 一張 fulfillment 一行 + tag + 金額 + currency
                                if (key === 'rebuild_fulfillments' || key === 'build_fulfillments') {
                                  // 只顯示「重建/建立後」那張（created）；舊張（destroyed）不再列。
                                  // rebuild_fulfillments → [重建] tag；build_fulfillments → [建立] tag。
                                  const created = Array.isArray(params.created) ? params.created : [];
                                  const amountLabel = t('models.fulfillment.total_amount', { defaultValue: '金額' });
                                  const codLabel = t('models.fulfillment.is_cod', { defaultValue: '代收貨款' });
                                  const isRebuild = key === 'rebuild_fulfillments';
                                  const tagLabel = isRebuild
                                    ? t('shared.orders.detail.histories.rebuild_label', { defaultValue: '重建' })
                                    : t('shared.orders.detail.histories.rebuild_created_label', { defaultValue: '建立' });
                                  const tagColor: 'processing' | 'success' = isRebuild ? 'processing' : 'success';
                                  return (
                                    <div>
                                      <strong>{t(`enums.order_history.action.${key}`, { defaultValue: key })}</strong>
                                      <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}>
                                        {created.map((f: any, i: number) => (
                                          <div key={`created-${f.id || f.number || f.name || i}`} style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: i === 0 ? 0 : '4px' }}>
                                            <Tag color={tagColor} style={{ fontSize: '11px', margin: 0 }}>{tagLabel}</Tag>
                                            <strong>{f.name || f.number}</strong>
                                            {f.total_amount != null && (
                                              <span style={{ color: '#999' }}>
                                                {amountLabel}: {formatAmount(f.total_amount, f.currency)}{f.currency ? ` ${f.currency}` : ''}
                                              </span>
                                            )}
                                            {f.is_cod === true && (
                                              <Tag color="orange" style={{ fontSize: '11px', margin: 0 }}>{codLabel}</Tag>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                }
                                // 電商串接建單 / 更新（ec_platform_create / ec_platform_update）：
                                // description 帶 module_name / account_name，收斂成「平台名(帳號名)」。
                                if (key === 'ec_platform_create' || key === 'ec_platform_update') {
                                  const accountName = typeof params.account_name === 'string' ? params.account_name.trim() : '';
                                  const moduleName = typeof params.module_name === 'string' ? params.module_name : '';
                                  const platformName = moduleName
                                    ? t(`enums.ec_account.platform.${moduleName}`, { defaultValue: moduleName })
                                    : '';
                                  params.platform_label = platformName && accountName
                                    ? `${platformName}(${accountName})`
                                    : (platformName || accountName || '-');
                                }
                                return <span>{t(`enums.order_history.action.${key}`, params) as string}</span>;
                              }
                              const descKey = typeof h.description === 'string' ? h.description : '';
                              return <span>{t(`shared.orders.detail.histories.${descKey}`, { ...h.params, defaultValue: descKey }) as string}</span>;
                            })()
                          ) : h.action ? (
                            <strong>{t(`enums.order_history.action.${h.action}`, { defaultValue: h.action })}</strong>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                        {parsedDescription && typeof parsedDescription === 'object' && parsedDescription.cascaded_from_order_name && (
                          <div style={{ fontSize: '12px', color: '#fa8c16', marginTop: '4px' }}>
                            {t('shared.orders.detail.histories.correct_order_cascaded_from', { name: parsedDescription.cascaded_from_order_name })}
                          </div>
                        )}
                        {fieldsChanged && (
                          <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #d9d9d9', borderRadius: '4px', fontSize: '12px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{t('models.history.fields_changed_label')}:</div>
                            {Object.entries(fieldsChanged).map(([field, raw]) => {
                              // 支援 [old, new] 陣列格式；非陣列值視為新值、舊值補 null
                              const changes: [unknown, unknown] = Array.isArray(raw) ? [raw[0], raw[1]] : [null, raw];
                              return (
                                <div key={field}>
                                  <strong>{t(`models.order.${field}`, { defaultValue: field })}:</strong>
                                  <div style={{ marginTop: '4px' }}>
                                    <Tag style={{ color: 'lightgrey', fontSize: '12px' }}>{formatHistoryValue(translateHistoryValue(t, field, changes[0]))}</Tag>
                                    <ArrowRightOutlined style={{ margin: '0 8px', fontSize: '12px' }} />
                                    <Tag color="green" style={{ fontSize: '12px' }}>{formatHistoryValue(translateHistoryValue(t, field, changes[1]))}</Tag>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(h.operator_name || h.user_name) && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {h.operator_name
                              ? <>{t('models.history.by_operator')}: {h.operator_name}</>
                              : <>{t('models.history.by_user')}: {h.user_name}</>}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{formatDateTime(h.created_at)}</div>
                      </div>
                    )
                  });
                  })}
                />
              ) : (
                <Empty description={t('common.table.no_data')} />
              ),
            },
            {
              key: 'api_logs',
              label: t('models.order.tabs.logs'),
              // 整合三來源（third_party / partner / webhook）並對 third_party
              // 批次回應走 RFC 6901 pointer 切片 — 一筆 row 只看到屬於這張訂單
              // 的部分，不會看到同批次其他訂單的資料。
              children: apiLogsTabActive ? (
                <IntegrationLogPanel
                  orderId={order.id}
                  orderService={orderService}
                />
              ) : null,
            },
          ]}
        />
      </Card>

      {/* Customer Information */}
      <Card
        title={t(`shared.orders.detail.sections.receiver_info`)}
        style={{ marginBottom: '16px' }}
        size="small"
      >
        <Descriptions column={3} bordered size="small">
          <Descriptions.Item label={t(`models.order.receiver_name`)}>
            {order.receiver_name}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.phone`)} span={2}>
            {order.phone}
          </Descriptions.Item>
          <Descriptions.Item label={t(`models.order.full_address`)} span={3}>
            {order.full_address || '-'}
          </Descriptions.Item>
          {!!order.email && (
            <Descriptions.Item label={t(`models.order.email`)}>
              {order.email}
            </Descriptions.Item>
          )}
          {!!order.convenience_store_code && (
            <Descriptions.Item label={t(`models.order.convenience_store_code`)}>
              {order.convenience_store_code}
            </Descriptions.Item>
          )}
          {!!order.delivery_time && (
            <Descriptions.Item label={t(`models.order.delivery_time`)}>
              {t(`enums.order.delivery_time.${order.delivery_time.toLowerCase()}`)}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Shipments */}
      {order.shipments && order.shipments.length > 0 && (
        <Card
          title={t(`shared.orders.detail.sections.shipping_info`)}
          style={{ marginBottom: '16px' }}
          size="small"
        >
          <Table
            dataSource={order.shipments}
            columns={shippingColumns as any}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )}
      {/* Order Items and Logs Tabs */}
      <Card style={{ marginBottom: '16px' }} size="small">
        <Tabs
          defaultActiveKey="items"
          items={[
            {
              key: 'items',
              label: t(`models.order.tabs.items`),
              children: (
                <Table
                  dataSource={processedOrderItems}
                  columns={visibleOrderItemColumns as any}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  expandable={{
                    rowExpandable: (record: any) =>
                      Array.isArray(record.inventories) && record.inventories.length > 0,
                    expandedRowRender: (record: any) => (
                      <Table
                        columns={inventoryModalColumns as any}
                        dataSource={record.inventories}
                        pagination={false}
                        rowKey="id"
                        size="small"
                        bordered
                      />
                    ),
                  }}
                  summary={(pageData) => {
                    const totalQuantity = pageData.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0)
                    const totalShipped = pageData.reduce((sum: number, item: any) => sum + (item.shipped_quantity || 0), 0)
                    const qtyColIndex = visibleOrderItemColumns.findIndex(c => c.key === 'quantity')
                    const remainingCols = visibleOrderItemColumns.length - qtyColIndex - 2 // -2 for quantity + shipped_quantity
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={qtyColIndex + 1}>
                            <strong style={{ fontSize: 16 }}>{t('common.total')}</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={qtyColIndex} align="right">
                            {allOrderInventories.length > 0 ? (
                              <Button type="link" size="small" style={{ padding: 0, fontSize: 28, fontWeight: 'bold' }} onClick={() => showAllInventories(allOrderInventories)}>
                                {totalQuantity}
                              </Button>
                            ) : (
                              <strong style={{ fontSize: 28 }}>{totalQuantity}</strong>
                            )}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={qtyColIndex + 1} align="right">
                            <strong style={{ fontSize: 28 }}>{totalShipped}</strong>
                          </Table.Summary.Cell>
                          {Array.from({ length: Math.max(0, remainingCols) }, (_, i) => (
                            <Table.Summary.Cell key={i} index={qtyColIndex + 2 + i} />
                          ))}
                        </Table.Summary.Row>
                      </Table.Summary>
                    )
                  }}
                />
              ),
            },
            {
              key: 'fulfillments',
              label: t(`models.order.tabs.fulfillments`),
              children: (
                <FulfillmentsPanel
                  fulfillments={order.fulfillments || []}
                  role={role}
                  currentOrderId={order.id}
                  onReload={() => revalidator.revalidate()}
                  merchantWarehouses={order.merchant?.warehouses || []}
                  currentWarehouse={currentWarehouse}
                  attributeLabelByName={attributeLabelByName}
                />
              ),
            },
            {
              key: 'serial_numbers',
              label: t(`models.order.tabs.serial_numbers`),
              children: order.serial_numbers && order.serial_numbers.length > 0 ? (
                <Collapse defaultActiveKey={order.serial_numbers.map((g: any) => g.parcel_id || 'unpacked')}>
                  {order.serial_numbers.map((group: any) => (
                    <Collapse.Panel
                      key={group.parcel_id || 'unpacked'}
                      header={group.parcel_id ? `${t('models.parcel.name')} #${group.parcel_id}` : ''}
                    >
                      {group.products.map((product: any) => (
                        <div key={product.id} style={{ marginBottom: '16px' }}>
                          <div style={{ fontWeight: 500, marginBottom: '8px' }}>
                            {product.name} ({product.sku})
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {product.serial_numbers.map((sn: any) => (
                              <Tag key={sn}>{sn}</Tag>
                            ))}
                          </div>
                        </div>
                      ))}
                    </Collapse.Panel>
                  ))}
                </Collapse>
              ) : (
                <Empty description={t('common.table.no_data')} />
              ),
            },
            {
              key: 'value_added_services',
              label: t(`models.order.tabs.value_added_services`),
              children: role === 'operator' ? (
                <ValueAddedServicesCardAny
                  record={order}
                  vasService={{
                    add: (recordId: any, data: any) => orderService.addValueAddedService(recordId, data),
                    update: (recordId: any, serviceId: any, data: any) => orderService.updateValueAddedService(recordId, serviceId, data),
                    delete: (recordId: any, serviceId: any) => orderService.deleteValueAddedService(recordId, serviceId),
                  }}
                  valueAddedServiceService={valueAddedServiceService}
                  onUpdate={() => revalidator.revalidate()}
                  editable={true}
                  showCard={false}
                />
              ) : (
                order.value_added_services && order.value_added_services.length > 0 ? (
                  <Table
                    dataSource={order.value_added_services}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      {
                        title: t(`models.value_added_service.title`),
                        dataIndex: 'title',
                        key: 'title',
                      },
                      {
                        title: t(`models.value_added_service.price`),
                        dataIndex: 'price',
                        key: 'price',
                        align: 'right',
                      },
                      {
                        title: t(`models.value_added_service.quantity`),
                        dataIndex: 'quantity',
                        key: 'quantity',
                        align: 'right',
                      },
                      {
                        title: t(`models.value_added_service.subtotal`),
                        dataIndex: 'subtotal',
                        key: 'subtotal',
                        align: 'right',
                      },
                    ]}
                    summary={(pageData) => {
                      const total = pageData.reduce((sum: number, item: any) => sum + (item.subtotal || 0), 0)
                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0}>
                              <strong>{t(`models.value_added_service.total`)}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1}></Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              <strong>{total.toFixed(2)}</strong>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )
                    }}
                  />
                ) : (
                  <Empty description={t('common.table.no_data')} />
                )
              ),
            },
            // 附件 tab：operator 端才支援（admin orderService 無附件 endpoint）
            ...(orderService.createAttachment ? [{
              key: 'attachments',
              label: t(`models.order.tabs.attachments`),
              children: (
                <OrderAttachments
                  attachments={order.attachments || []}
                  orderId={order.id}
                  service={orderService}
                  onUpdate={() => revalidator.revalidate()}
                  readOnly
                />
              ),
            }] : []),
          ]}
        />
      </Card>

      {/* Reprint Label Modal (Operator only) */}
      {role === 'operator' && (
        <ReprintLabelModal
          visible={reprintModalVisible}
          onCancel={() => setReprintModalVisible(false)}
          onConfirm={handleReprintLabel}
          order={order}
          loading={reprinting}
        />
      )}

      {/* Hold Order Modal */}
      <HoldOrderModal
        visible={holdModalVisible}
        onCancel={() => setHoldModalVisible(false)}
        onConfirm={handleHold}
        orderName={order.name}
        confirmLoading={holding}
      />

      {/* Release Hold Modal */}
      <ReleaseHoldModal
        visible={releaseModalVisible}
        onCancel={() => setReleaseModalVisible(false)}
        onConfirm={handleReleaseHolds}
        count={1}
        confirmLoading={releasing}
      />

      {/* Shipment Status History Drawer */}
      <Drawer
        title={
          historyDrawerShipment
            ? t('shared.orders.detail.shipments.history_drawer_title', {
                tracking: historyDrawerShipment.tracking_number || historyDrawerShipment.id,
              })
            : t('shared.orders.detail.shipments.history_button')
        }
        open={!!historyDrawerShipment}
        onClose={() => setHistoryDrawerShipment(null)}
        width={480}
      >
        {historyDrawerShipment && (() => {
          const histories = historyDrawerShipment.status_histories || []
          if (histories.length === 0) {
            return <Empty description={t('shared.orders.detail.shipments.history_empty')} />
          }
          const statusColor = SHIPMENT_STATUS_COLORS
          return (
            <Timeline
              mode="left"
              items={histories.map((h: any) => {
                const detailMatch = typeof h.detail === 'string' ? h.detail.match(/^\s*\[([^\]]+)\]\s*(.*)$/) : null
                const detailCode = detailMatch?.[1]
                const detailText = detailMatch ? detailMatch[2] : h.detail
                return {
                  color: statusColor[h.status] || 'gray',
                  label: (
                    <Space direction="vertical" size={2}>
                      <strong>{formatDateTime(h.occurred_at || h.created_at)}</strong>
                      <strong>{shipmentStatusLabel(h.status)}</strong>
                    </Space>
                  ),
                  children: (
                    <Space direction="vertical" size={2}>
                      {detailCode && <Tag style={{ margin: 0 }}>{detailCode}</Tag>}
                      {detailText && <span style={{ color: '#595959' }}>{detailText}</span>}
                    </Space>
                  ),
                }
              })}
            />
          )
        })()}
      </Drawer>

      {/* Correct Order Info Modal */}
      <Modal
        title={t('shared.orders.detail.correct_modal.title')}
        open={correctModalVisible}
        onCancel={() => setCorrectModalVisible(false)}
        onOk={handleCorrectSubmit}
        confirmLoading={correcting}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        width={600}
      >
        <Alert
          message={t('shared.orders.detail.correct_modal.description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {correctionSiblingOrders.length > 0 && (
          <Alert
            message={t('shared.orders.detail.correct_modal.cascade_warning', { count: correctionSiblingOrders.length })}
            description={correctionSiblingOrders.map(o => o.name).join('、')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={correctForm} layout="vertical">
          <FormReceiverInformation form={correctForm} contactAddressService={contactAddressService} merchantId={order.merchant?.id || order.merchant_id} />

          <Form.Item name="convenience_store_code" label={t('models.order.convenience_store_code')}>
            <Input />
          </Form.Item>

          <Divider />

          <Form.Item name="scheduled_shipping_date" label={
            <span>
              {t('models.order.scheduled_shipping_date')}{' '}
              <Tooltip title={t('shared.orders.form.scheduled_shipping_date_tooltip')}>
                <span style={{ color: '#999', cursor: 'help' }}>ⓘ</span>
              </Tooltip>
            </span>
          }>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="delivery_date" label={t('models.order.delivery_date')}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="delivery_time" label={t('models.order.delivery_time')}>
            <Radio.Group>
              <Radio.Button value="AM">{t('enums.order.delivery_time.am')}</Radio.Button>
              <Radio.Button value="PM">{t('enums.order.delivery_time.pm')}</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Divider />

          <Form.Item name="note" label={t('models.order.note')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Duplicate Order Modal */}
      <Modal
        title={t('shared.orders.detail.actions.duplicate_confirm_title')}
        open={duplicateModalVisible}
        onCancel={() => setDuplicateModalVisible(false)}
        onOk={handleDuplicateConfirm}
        confirmLoading={duplicateLoading}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        okButtonProps={{ disabled: !duplicateName.trim() || !!duplicateNameError }}
      >
        <p style={{ marginBottom: 12 }}>
          {t('shared.orders.detail.actions.duplicate_confirm_content', { orderName: order.name })}
        </p>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
            {t('shared.orders.detail.actions.duplicate_name_label')}
          </label>
          <Input
            value={duplicateName}
            onChange={(e) => { setDuplicateName(e.target.value); setDuplicateNameError(null) }}
            status={duplicateNameError ? 'error' : undefined}
            autoFocus
          />
          {duplicateNameError && (
            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{duplicateNameError}</div>
          )}
        </div>
      </Modal>

      {renderInventoryModals()}
    </div>
  )
}

export default OrderShow
